import { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { useInstrumentsEarliestAndLatestDates } from "connected-components/dashboard/helpers"
import { getDashboardPeriodStartDates } from "helpers/dates"
import { useShouldAdjustDates } from "hooks/should-adjust-dates"
import { getToday } from "helpers/types/dates"
import { useLastIntradayBatch } from "hooks/intraday"
import { calculateIntradayState } from "./use-intraday-state"
import { calculateDateAdjustments } from "../helpers/date-calculations"

const CUSTOM_PERIOD = "CUSTOM"

export function useAdjustedDashboardDates({
  modelMetadata,
  instrumentsWithMetadata,
  period,
  startDate,
  endDate,
  useLatestDate,
  setUseLatestDate,
  setPeriod,
  setBoundaryDates,
  setPerformanceStartDate,
  setPerformanceEndDate,
  trackPeriodShortcutDateChanged,
  instrument,
  isIntradayEnabled,
  isIntradayAnalyticsEnabled = false,
}) {
  const [lastIntradayBatch, intradayBatchStatus] = useLastIntradayBatch(instrument, instrumentsWithMetadata?.original?.intradayEnabled)
  const unmodeledDates = useMemo(() => modelMetadata?.availability.unmodeledDates || [], [modelMetadata?.availability.unmodeledDates])
  const [boundaryEarliestDate, boundaryLatestDate] = useInstrumentsEarliestAndLatestDates(modelMetadata, instrumentsWithMetadata)
  const [shouldAdjustDates] = useShouldAdjustDates(instrument, boundaryEarliestDate)
  const [initialEndDate] = useState(endDate)
  const today = getToday()

  const [{ isIntradayMode, isIntradayBatchAvailable, isEndDateToday }] = useMemo(() => {
    return calculateIntradayState(isIntradayEnabled, endDate, today, lastIntradayBatch, intradayBatchStatus)
  }, [isIntradayEnabled, endDate, today, lastIntradayBatch, intradayBatchStatus])

  const dateAdjustments = useMemo(() => {
    return calculateDateAdjustments({
      endDate,
      boundaryLatestDate,
      boundaryEarliestDate,
      isIntradayMode,
      initialEndDate,
      useLatestDate,
      period,
      startDate,
      isIntradayEnabled,
      intradayBatchStatus,
      lastIntradayBatch,
      isIntradayAnalyticsEnabled,
    })
  }, [endDate, boundaryLatestDate, boundaryEarliestDate, isIntradayMode, initialEndDate, useLatestDate, period, startDate, isIntradayEnabled, intradayBatchStatus, lastIntradayBatch, isIntradayAnalyticsEnabled])

  useEffect(() => {
    setBoundaryDates(boundaryEarliestDate, boundaryLatestDate)
  }, [boundaryEarliestDate, boundaryLatestDate, setBoundaryDates, modelMetadata, instrumentsWithMetadata])

  useEffect(() => {
    if (dateAdjustments.isLoading) return
    setPerformanceEndDate(dateAdjustments.performanceEndDate)
  }, [dateAdjustments.isLoading, dateAdjustments.performanceEndDate, setPerformanceEndDate])

  useEffect(() => {
    if (dateAdjustments.isLoading) return

    if (dateAdjustments.hasChanges) {
      setPeriod(dateAdjustments.finalPeriodState)
    }
  }, [dateAdjustments.isLoading, dateAdjustments.hasChanges, dateAdjustments.finalPeriodState, setPeriod])

  const { computedStartDate, computedPerformanceStartDate, datesAdjusted } = useMemo(() => {
    if (dateAdjustments.isLoading) {
      return {
        computedStartDate: startDate,
        computedPerformanceStartDate: startDate,
        datesAdjusted: false,
      }
    }

    const startDateAdjustments = getDashboardPeriodStartDates(
      startDate,
      dateAdjustments.effectiveEndDate,
      initialEndDate,
      period,
      boundaryEarliestDate,
      boundaryLatestDate,
      unmodeledDates,
      !!shouldAdjustDates, // Gets rid of the null
      isIntradayEnabled,
      isIntradayBatchAvailable,
      isEndDateToday,
    )

    // For CUSTOM periods, preserve user's explicit startDate if the function didn't apply necessary adjustments (weekends, boundaries, holidays)
    if (period === CUSTOM_PERIOD) {
      const userStartMoment = moment(startDate)
      const computedStartMoment = moment(startDateAdjustments.computedStartDate)

      // Only use the computed date if it was adjusted for valid reasons (weekends, boundaries, holidays), otherwise preserve user intent
      const wasAdjustedForValidReasons =
        userStartMoment.isoWeekday() > 5 // Weekend adjustment
        || userStartMoment.isBefore(moment(boundaryEarliestDate)) // Boundary clamping
        || startDateAdjustments.datesAdjusted // Other adjustments (holidays, etc.)

      if (!wasAdjustedForValidReasons && !userStartMoment.isSame(computedStartMoment, "day")) {
        // Function computed a different date for invalid reasons, preserve user intent
        return {
          computedStartDate: startDate,
          computedPerformanceStartDate: startDate,
          datesAdjusted: false,
        }
      }
    }

    return startDateAdjustments
  }, [dateAdjustments.isLoading, startDate, dateAdjustments.effectiveEndDate, initialEndDate, period, boundaryEarliestDate, boundaryLatestDate, unmodeledDates, shouldAdjustDates, isIntradayEnabled, isIntradayBatchAvailable, isEndDateToday])

  useEffect(() => {
    if (dateAdjustments.isLoading) return
    setPerformanceStartDate(computedPerformanceStartDate)
  }, [dateAdjustments.isLoading, computedPerformanceStartDate, setPerformanceStartDate])

  useEffect(() => {
    if (dateAdjustments.isLoading) return

    if (period !== CUSTOM_PERIOD && startDate !== computedStartDate) {
      setPeriod({
        period: period,
        startDate: computedStartDate,
        endDate: dateAdjustments.effectiveEndDate,
      })
      trackPeriodShortcutDateChanged(startDate, computedStartDate, dateAdjustments.effectiveEndDate, period)
    }
  }, [dateAdjustments.isLoading, computedStartDate, startDate, dateAdjustments.effectiveEndDate, period, setPeriod, trackPeriodShortcutDateChanged, setUseLatestDate, boundaryLatestDate])

  if (dateAdjustments.isLoading) {
    return {
      adjustedStartDate: startDate,
      endDate: endDate,
      earliestDate: boundaryEarliestDate,
      latestDate: boundaryLatestDate,
      unmodeledDates,
      datesAdjusted: false,
      isLoading: true,
    }
  }

  return {
    adjustedStartDate: computedStartDate,
    endDate: dateAdjustments.effectiveEndDate,
    earliestDate: boundaryEarliestDate,
    latestDate: boundaryLatestDate,
    unmodeledDates,
    datesAdjusted,
    isLoading: false,
  }
}
